import { FCC } from "@/types/common";
import { UseVisibleReturn } from "@/hooks/useVisible";
import { Box, CircularProgress, Modal, Stack, styled, Typography } from "@mui/material";
import { background, black, divider, text } from "@/theme/colors";
import { IconBox } from "@/components/elements/IconBox";
import { IconClose, IconCompanyListImport } from "@/assets/icons";
import { rounded } from "@/theme/variables";
import { DropZone } from "@/components/elements/DropZone";
import { Button } from "@/components/elements/Button";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useState } from "react";
import { TExtendFile } from "@/types/file";
import { downloadSample, validateFileFormat } from "@/features/talent/utils/excel";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { validateImportTalentApi } from "@/api/services/main/talent";
import { TTalentRowExcel } from "@/features/talent/types/talentExcel";
import { AppRoutes } from "@/routes/config";
import { useGetCompanySpecificIndustryList } from "@/api/services/global/master";

type TImportPersonnelModalProps = {
  modal: UseVisibleReturn;
  isLoading?: boolean;
};

type TErrorItem = {
  message: string;
};

export const ImportTalentModal: FCC<TImportPersonnelModalProps> = ({ modal, isLoading }) => {
  const navigate = useNavigate();
  const [_progress, setProgress] = useState(0);
  const [isSubmit, setIsSubmit] = useState(false);
  const [file, setFile] = useState<File | null>();
  const [errorList, setErrorList] = useState<string[]>([]);

  const { companySpecificIndustryOptions } = useGetCompanySpecificIndustryList();

  const handleErrorImportCsv = (errorContainer: unknown) => {
    if (typeof errorContainer === "object" && errorContainer !== null) {
      if ("error" in errorContainer) {
        const listErrors = (errorContainer as { error: unknown }).error;
        if (Array.isArray(listErrors)) {
          setErrorList(listErrors.map((item: TErrorItem) => item.message));
        }
      } else if ("message" in errorContainer) {
        setErrorList([(errorContainer as { message: unknown }).message as string]);
      }
    } else {
      setErrorList(["Unknown error"]);
    }
  };

  const onClose = () => {
    setProgress(0);
    setIsSubmit(false);
    setFile(null);
    setErrorList([]);
    modal.close();
  };
  const handleChangeFiles = async (value: TExtendFile | TExtendFile[] | null) => {
    const files = [value ?? []].flat();
    if (!files[0]) {
      setFile(null);
      return;
    }
    const data = files[0];
    setFile(data);
    setErrorList([]);
  };

  const readFileImport = async (file: File): Promise<[][]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const ext = file.name.split(".").pop()?.toLowerCase();
          let workbook: XLSX.WorkBook;

          if (ext === "csv") {
            workbook = XLSX.read(data as string, {
              type: "string",
              codepage: 65001, // UTF-8
              raw: true,
            });
          } else if (ext === "xlsx" || ext === "xlsm") {
            workbook = XLSX.read(data, {
              type: "array",
            });
          } else {
            throw new Error("Unsupported file type");
          }

          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const rows: [][] = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: "",
            raw: false,
          });
          validateFileFormat(rows[0]);
          resolve(rows);
        } catch (err) {
          reject(err);
        }
      };

      const ext = file.name.split(".").pop()?.toLowerCase();
      if (ext === "csv") reader.readAsText(file, "utf-8");
      else reader.readAsArrayBuffer(file);
    });
  };

  const handleImportFile = async () => {
    if (!file) return;
    try {
      setProgress(0);
      setIsSubmit(true);

      const rows: [][] = await readFileImport(file);
      const totalRows = rows.length;
      const results = [];
      const arrayKey = {
        recruitmentStatus: "",
        personalInCharge: "",
        fullName: "",
        birthday: "",
        nickName: "",
        talentNumber: "",
        nationality: "",
        gender: "",
        phoneNumber: "",
        email: "",
        location: "",
        zipCode: "",
        address: "",
        country: "",
        residenceStatus: "",
        periodOfStay: "",
        specifiedSkillFirstStartDate: "",
        specifiedSkillFirstRemainingTerm: "",
        passportNumber: "",
        passportExpiryDate: "",
        specificBusinessClassificationFirst: "",
        specificBusinessClassificationTypeFirst: "",
        languageCertificates: "",
        languageTestOther: "",
        specificSkillCertificates: "",
        majorName: "",
        jobName: "",
        memo: "",
        interviewEvaluation: "",
        familyInformation: "",
        desiredJobType: "",
        desiredWorkLocation: "",
        expectSalary: "",
        availableFrom: "",
        otherRequest: "",
        internalMemo: "",
        interviewDate: "",
      };
      const keys = Object.keys(arrayKey);
      for (let i = 1; i < totalRows; i++) {
        const row: string[] = rows[i];
        if (row.length === 0) continue;

        if (row.every((cell) => String(cell).trim() === "")) {
          continue;
        }

        results.push(row);

        const percent = Math.round(((i + 1) / totalRows) * 75);
        setProgress(percent);

        await new Promise((r) => setTimeout(r, 0));
      }
      setProgress(75);
      const formattedResults: TTalentRowExcel[] = results.map((row) => {
        return Object.fromEntries(keys.map((key, index) => [key, row[index] ?? ""])) as TTalentRowExcel;
      });
      const { data: talents } = await validateImportTalentApi(formattedResults);
      setProgress(100);
      modal.close();
      navigate(AppRoutes.talentImport, { state: { talents } });
    } catch (err) {
      setProgress(0);
      setIsSubmit(false);
      handleErrorImportCsv(err);
    }
  };
  return (
    <Modal
      open={modal.visible}
      hideBackdrop={false}
      sx={{
        position: "fixed",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <ModalContainer width={920}>
        <Stack
          bgcolor={background.disable}
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          height="53px"
          sx={{
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
            padding: "16px",
          }}
        >
          <Typography variant="body14Light" color={text.primary}>
            人材情報 | 一括インポート
          </Typography>
          <IconBox onClick={onClose} sx={{ p: 0 }}>
            <IconClose color={black[80]} fontSize={24} />
          </IconBox>
        </Stack>
        <Stack
          sx={{
            padding: "32px 16px ",
            gap: "24px",
          }}
        >
          {!isSubmit && (
            <Box borderColor={divider.middle} borderRadius={rounded.sm}>
              <DropZone
                options={{ multiple: false }}
                onChange={handleChangeFiles}
                rules={{
                  maxFile: 1,
                  acceptMimeTypes: [
                    "text/csv",
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    "application/vnd.ms-excel.sheet.macroEnabled.12",
                  ],
                  maxSize: 10 * 1000 * 1000,
                }}
                isShowErr={false}
              >
                <Typography variant="body14Light" whiteSpace="pre-wrap" textAlign="center">
                  アップロードするファイル（csv、xlsx、xlsm）をドラッグ&ドロップ
                </Typography>
                <Typography variant="body14Light" width={920} textAlign="center">
                  または
                </Typography>
                <Button type="button" variant="outline">
                  ファイルを選択する
                </Button>
              </DropZone>
              {errorList.length > 0 && (
                <Stack
                  sx={{ mt: "32px", p: "8px", background: background.error, color: text.error, maxHeight: "160px", overflowY: "auto" }}
                  borderRadius={rounded.xs}
                >
                  {errorList.map((item, index) => (
                    <Typography key={index} component="p" variant="cap12">
                      {item}
                    </Typography>
                  ))}
                </Stack>
              )}
            </Box>
          )}
          {isSubmit && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", height: "150px" }}>
              <Box sx={{ height: 60 }}>
                <CircularProgress />
              </Box>
              <Typography variant="body14Light" textAlign="center">
                処理中
              </Typography>
            </Box>
          )}
        </Stack>
        <hr />
        <Stack
          flexDirection="row"
          gap="16px"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            padding: "16px 32px",
          }}
        >
          <Stack flexDirection="row" gap="16px" justifyContent="flex-start">
            <Button variant="ghost" onClick={onClose}>
              キャンセル
            </Button>
          </Stack>
          <Stack flexDirection="row" gap="16px" justifyContent="flex-end">
            {!isSubmit && (
              <Button onClick={() => downloadSample(companySpecificIndustryOptions)} variant="outline" startIcon={<FileDownloadIcon />}>
                テンプレートをダウンロード
              </Button>
            )}
            <Button
              onClick={handleImportFile}
              isLoading={isLoading}
              disabled={isSubmit || !file || errorList.length > 0}
              startIcon={<IconCompanyListImport />}
            >
              {isSubmit ? "ファイルを処理中..." : "インポート"}
            </Button>
          </Stack>
        </Stack>
      </ModalContainer>
    </Modal>
  );
};

const ModalContainer = styled(Box)`
  min-width: 417px;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  max-height: 90vh;
  outline: none; /* Remove default focus outline */

  &:focus {
    outline: none;
  }
`;
